import {LitElement, html, css} from 'lit-element';
import { localizeStringForLanguage } from '../../util/util-lit/util-lit.js';
import { renderIconWithName } from '../../ui/ui-icon/ui-icon.js';

class MediaImage extends LitElement {
    static get properties () {
        return {
            captionOpen: {
                type: Boolean
            },
            caption: {
                type: String
            },
            credit: {
                type: String
            },
            isLede: {
                attribute: 'is-lede',
                type: Boolean
            },
            imageAlt: {
                attribute: 'image-alt',
                type: String
            },
            imageSet: {
                attribute: 'image-set',
                type: String
            },
            orientation: {
                type: String
            }
        };
    }


    static get styles () {
        return [
            css`
            :host {
                display: block;
            }

            #imgContent {
                width: 100%;
            }

            .media-image-wrapper {
                margin: 0 auto;
                position: relative;
                width: 100%;
            }

            .img-caption {
                color: #626262;
                font-family: 'Unify Sans', Helvetica, Arial, sans-serif;
                font-size: calc(14px + (16 - 14) * ((100vw - 320px) / (1200 - 320)));
                font-weight: 400;
                font-style: normal;
                line-height: 1.3;
                margin-top: var(--spacer-twelve, 12px);
                padding: 0 var(--one-column, 8.33333333%);
            }

            .img-caption.closed {
                opacity: 0;
            }
            
            .img-caption.short-caption {
                margin-bottom: -55px;
            }
            .img-caption.open {
                opacity: 1;
                margin-bottom: 55px;
                position: relative;
            }
            .img-caption.open.short-caption {
                margin-bottom: 30px;
            }
            .img-caption.open.inline {
                margin: 7px 0 0 0;
            }
            .media-image-credit {
                color: #626262;
                font-family: 'Unify Sans', Helvetica, Arial, sans-serif;
                font-size: calc(12px + (14 - 12) * ((100vw - 320px) / (1200 - 320)));
                font-weight: 400;
                font-style: normal;
                line-height: var(--line-height-default, 1.15);
                margin: 0;
                padding-top: 5px;
                text-transform: uppercase;
            }

            .caption-toggle {
                color: var(--type-secondary, #444);
                font-family: 'Unify Sans', Helvetica, Arial, sans-serif;
                font-size: calc(14px + (16 - 14) * ((100vw - 320px) / (1200 - 320)));
                font-style: normal;
                line-height: var(--line-height-default, 1.15);
                margin: 0;
                color:  var(--color-white, #fff);
                position: absolute;
                bottom: 37px;
                font-weight: var( --font-weight-bold, 900);
                padding-top: 40px;
                padding-left: var( --one-column, 8.33333333%);
                padding-bottom: 10px;
                padding-right: 25px;

                -webkit-backface-visibility: hidden;
                transition: all  var(--speed-fast, 0.25s) ease; 
                -webkit-transform-style: preserve-3d;
            }
            .caption-toggle.open {
                bottom: var(--spacer-twelve, 12px);
            }
            .caption-toggle:after {
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                border-top: 7px solid var(--color-white, #fff);
                content: '';
                display: inline-block;
                height: 0;
                margin-left: 5px;
                width: 0;
            }
            .caption-toggle.open:after {
                border-bottom: 7px solid var(--color-white, #fff);
                border-top: 0 none;
            }
            .open .show-caption,
            .closed .hide-caption {
                display: none;
            }
            .open .hide-caption,
            .closed .show-caption {
                cursor: pointer;
                display: inline-block;

                /* removing space between inline-block elements */
                margin-right: -4px;
            }

            .caption-container a {
                color: #626262;
                font-weight: 600;
                text-decoration: none;
                white-space: nowrap;
            }

            .icon-span {
                display: inline-block;
                fill: #626262;
                position: relative;
                top: 4px;
            }
            `
        ];
    }

    constructor () {
        super();

        this.captionOpen = false;
        this.caption = '';
        this.credit = '';
        this.isLede = false;
        this.imageAlt = '';
        this.imageSet = '';
        this.orientation = 'horizontal';
    }

    
    getThumbnail (imageUrl, width, height = '', additionalParams = '') {
        if (!imageUrl) {
            return '';
        }
        if (additionalParams === 'external') return `${ga_data.site.hosts.gannettCDN}/-ip-/${width}x${height}/${imageUrl}`;

        let resizeParams = '';
        if ( additionalParams && additionalParams.indexOf('crop=') > -1 || !height) {
            resizeParams = `width=${width}`;
        } else {
            resizeParams = `width=${width}&height=${height}`;
        }
        if (additionalParams && additionalParams !== '') resizeParams = resizeParams + `&${additionalParams}`;
        if (imageUrl.indexOf('http://videos.usatoday.net') === 0) return `${ga_data.site.hosts.gannettCDN}/-ip-/${width}x${height}/${imageUrl}${imageUrl.indexOf('?') > -1 ? '&' : '?'}${resizeParams}`;
        if (imageUrl.indexOf('http://www.gannett-cdn.com') === 0) imageUrl = imageUrl.replace('http', 'https');
        if (imageUrl.indexOf('https://www.gannett-cdn.com') === 0 || imageUrl.indexOf('https://assets.gannettdigital.com') === 0) {
            return `${imageUrl}${imageUrl.indexOf('?') > -1 ? '&' : '?'}${resizeParams}`;
        } else {
            return `${ga_data.site.hosts.gannettCDN}/-ip-//${imageUrl}${imageUrl.indexOf('?') > -1 ? '&' : '?'}${resizeParams}`;
        }
    }

    getImageThumbnail (imageUrl, imageSet, orientation) {
        const DEFINED_SIZES = {
            'small': {
                'horizontal': {
                    '1x': {width: 640, height: 480},
                    '2x': {width: 640, height: 480}
                },
                'vertical': {
                    '1x': {width: 640, height: 854},
                    '2x': {width: 640, height: 854}
                }
            },
            'medium': {
                'horizontal': {
                    '1x': {width: 960, height: 720},
                    '2x': {width: 1920, height: 1440}
                },
                'vertical': {
                    '1x': {width: 770, height: 1030},
                    '2x': {width: 1540, height: 2060}
                }
            },
            'large': {
                'horizontal': {
                    '1x': {width: 1280, height: 960},
                    '2x': {width: 2560, height: 1920}
                },
                'vertical': {
                    '1x': {width: 1297, height: 1734},
                    '2x': {width: 1588, height: 2122}
                }
            }
        };
        const CROP_BREAKPOINT = 640;
        const LG_CROP_BREAKPOINT = 960;

        let multiplier = '1x',
            size = 'small',
            thumbnail = '';

        if (window.innerWidth > CROP_BREAKPOINT && window.innerWidth < LG_CROP_BREAKPOINT) {
            size = 'medium';
        } else if (window.innerWidth >= LG_CROP_BREAKPOINT) {
            size = 'large';
            multiplier = '2x';
        }

        imageUrl = imageUrl || this.getImageUrl(imageSet);

        if (imageUrl) {
            thumbnail = this.getThumbnail(imageUrl, DEFINED_SIZES[size][orientation][multiplier].width);
        }

        return thumbnail;
    }

    getImageUrl (imageSet = '') {
        const CROP_BREAKPOINT = 640;

        let urls = imageSet.split(', ')
            .map(c => c.split(' ').filter(x => !!x))
            .reduce((acc, [url, ratio]) => {
                acc[ratio] = url;
                return acc;
            }, {});

        let imageUrl = '';

        if(this.isLede) {
            if (window.innerWidth < CROP_BREAKPOINT) {
                imageUrl = urls['4:3'];
            }else{
                imageUrl = urls['16:9'];
            }
        } else {
            if (urls.bestCrop) {
                imageUrl = urls.bestCrop;
            } else {
                imageUrl = (this.orientation === 'vertical') ? urls['3:4'] : urls['4:3'];
            }
        }
        return imageUrl;
    }

    
    toggleMoreOrLess () {
        let showMore = this.shadowRoot.getElementById('showMore');
        let showLess = this.shadowRoot.getElementById('showLess');

        this.shadowRoot.getElementById('captionPartTwo').hidden = showMore.hidden;
        showLess.hidden = showMore.hidden;
        this.shadowRoot.getElementById('ellieTheEllipsis').hidden = !showMore.hidden;
        showMore.hidden = !showMore.hidden;
    }

    toggleCaption () {
        let captionToggle = this.shadowRoot.getElementById('captionToggle');
        this.captionOpen = !this.captionOpen;
        captionToggle.classList.toggle('closed');
        captionToggle.classList.toggle('open');
        this.shadowRoot.getElementById('caption').classList.toggle('closed');
        this.shadowRoot.getElementById('caption').classList.toggle('open');
    }

    firstUpdated () {
        if (this.isLede) {
            this.shadowRoot.getElementById('caption').classList.add('short-caption');
            this.shadowRoot.getElementById('caption').classList.toggle('closed');
        } else {
            this.captionChanged(this.caption);
        }

    }

    captionChanged (caption) {
        const TRUNCATION_LIMIT = 25;
        let captionArray = caption.trim().split(' ');
        if (captionArray.length > TRUNCATION_LIMIT ) {
             this.truncateCaption(captionArray);
        }
        this.shadowRoot.getElementById('caption').hidden = !(caption && caption.length);
    }

    truncateCaption (captionArray) {
        
        const TRUNCATION_LIMIT = 25;
        let captionPartOne = this.shadowRoot.getElementById('captionPartOne');
        let captionPartTwo = this.shadowRoot.getElementById('captionPartTwo');
        captionPartOne.innerHTML = captionArray.slice(0, TRUNCATION_LIMIT).join(' ');
        captionPartTwo.innerHTML = captionArray.slice(TRUNCATION_LIMIT, captionArray.length).join(' ');
        this.shadowRoot.getElementById('showMore').hidden = false;
        this.shadowRoot.getElementById('ellieTheEllipsis').hidden = false;
    }

    render () {
        let languageCfg = {
            'en': {
                'show-caption': 'Show caption',
                'hide-caption': 'Hide caption',
                'show-more': 'Show more',
                'show-less': 'Show less'
            },
            'es': {
                'show-caption': 'Mostrar leyenda',
                'hide-caption': 'Ocultar leyenda',
                'show-more': 'Mostrar más',
                'show-less': 'Muestra menos'
            }
        };

        let imageURL = this.getImageUrl(this.imageSet);
        return html`
        <div id="container" class="media-image-viewport">
            <div class="media-image-wrapper">
                <img id="imgContent" alt=${this.imageAlt} src=${this.getImageThumbnail(imageURL, this.imageSet, this.orientation)} />       
                ${
                    this.isLede ? 
                    html`                
                    <div id="captionToggle" ?caption-open=${this.captionOpen} class="caption-toggle closed" @click=${this.toggleCaption}>
                        <div class="show-caption">${localizeStringForLanguage(languageCfg, 'show-caption')}</div>
                        <div class="hide-caption">${localizeStringForLanguage(languageCfg, 'hide-caption')}</div>
                    </div>` : html``
                }
            </div>
            <div id="caption" class="img-caption">
                <div id="imageCaption" class="caption-container">
                    <span id="captionPartOne">${this.caption}</span><span hidden id="ellieTheEllipsis">&nbsp;&hellip;</span> 
                    <a href="javascript:;" id="showMore" hidden @click=${this.toggleMoreOrLess}>
                        ${localizeStringForLanguage(languageCfg, 'show-more')}&nbsp;<span class="icon-span">${renderIconWithName('iconCaretDownSM', 16)}</span>
                    </a>
                    <span id="captionPartTwo" hidden></span>&nbsp;
                    <a href="javascript:;" id="showLess" @click=${this.toggleMoreOrLess} hidden>
                        ${localizeStringForLanguage(languageCfg, 'show-less')}&nbsp;<span class="icon-span">${renderIconWithName('iconCaretUpSM', 16)}</span>
                    </a>
                </div>
                <div class="media-image-credit">${this.credit}</div>
            </div>
        </div>
        `;
    }
}

customElements.define('media-image', MediaImage);